import { getIntlLocale } from '@bofrak-backend/shared-i18n';
import {
  AccountTransaction,
  Big,
  bigSum,
  canonicalTransactionCategory,
  DailyCountItemStatus,
  DailyCountList,
  Debt,
  formatCurrency,
  InventoryChange,
  PaymentMethodType,
  PurchaseOrder,
  Shift,
  ShiftStatus,
  StoreInventoryValue,
  StoreProduct,
  StoreSnapshotsResult,
  TransactionCategory,
} from '@bofrak-backend/shared';
import {
  Box,
  Collapse,
  HStack,
  Icon,
  SimpleGrid,
  Spacer,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useMemo, useState } from 'react';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaLock,
  FaLockOpen,
} from 'react-icons/fa';
import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';
import { apiAdapter } from '../../api/backend';
import { mergeReversalCandidates } from '../ResponsiveTransactionTable';
import { merchantAtom, storeAtom } from '../../recoil/atoms';
import { ClientDebtsPageDrawer } from './ClientDebtsPageDrawer';
import { GrossProfitDrawer } from './GrossProfitDrawer';
import { InventoryGainLossDrawer } from './InventoryGainLossDrawer';
import { InventoryValueOverlay } from './InventoryValueOverlay';
import { MetricCard, SectionHeader } from './MetricCard';
import { MoneyDetailsDrawer } from './MoneyDetailsDrawer';
import { QueryErrorAlert } from './QueryErrorAlert';
import { SalesReportDrawer } from './SalesReportDrawer';
import { SupplierDebtsPageDrawer } from './SupplierDebtsPageDrawer';
import { TransactionCategoryDrawer } from './TransactionCategoryDrawer';
import { useSharedUiTranslation, type SharedUiT } from '../../../i18n';
import type { DashboardStatus } from '../../hooks/use-dashboard-queries';
import { combineStatus } from '../../utils/query-status';

const blinkAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
`;

const getMethodLabels = (t: SharedUiT): Record<string, string> => ({
  [PaymentMethodType.CASH]: t('Cash'),
  [PaymentMethodType.CARD]: t('Card'),
  [PaymentMethodType.MOBILE_MONEY]: t('Mobile Money'),
  [PaymentMethodType.BANK_TRANSFER]: t('Bank'),
  [PaymentMethodType.CHEQUE]: t('Cheque'),
  [PaymentMethodType.COUPON]: t('Coupon'),
  [PaymentMethodType.LOYALTY_POINTS]: t('Loyalty'),
  [PaymentMethodType.DEBT]: t('Debt'),
  [PaymentMethodType.OTHER]: t('Other'),
});

interface SummarySlideProps {
  salesMetrics: { netSales: number; grossProfit: number };
  netProfit: number;
  expenses: number;
  supplierMetrics: { paid: number; due: number };
  debtMetrics: { outstanding: number; paid: number };
  /** Per-request state: each card waits only for its own data. */
  status: DashboardStatus;
  /** Sales report request failure; shown instead of silent zeros. */
  salesReportError?: unknown;
  onRetrySalesReport?: () => void;
  isRetryingSalesReport?: boolean;
  dateRange: { start: Date | null; end: Date | null };
  storeValuation: { inventoryEnd: number };
  inventoryGainOrLoss: number;
  /** Value impact of manual cost edits on on-hand stock in the period. */
  costRevaluation?: number;
  accountTransactions: AccountTransaction[];
  /** Partner reversals for `accountTransactions` (API `reversals` field). */
  accountTransactionsReversals?: AccountTransaction[];
  inventoryValues: StoreInventoryValue[];
  allInventoryChanges: InventoryChange[];
  storeProducts: StoreProduct[];
  moneyStart: number;
  moneyEnd: number;
  snapshotsEnd?: StoreSnapshotsResult;
  debts: Debt[];
  purchaseOrders: PurchaseOrder[];
  dailyCountLists?: DailyCountList[];
}

export function SummarySlide({
  salesMetrics,
  netProfit,
  expenses,
  supplierMetrics,
  debtMetrics,
  status,
  salesReportError,
  onRetrySalesReport,
  isRetryingSalesReport,
  dateRange,
  storeValuation,
  inventoryGainOrLoss,
  costRevaluation = 0,
  accountTransactions,
  accountTransactionsReversals,
  inventoryValues,
  allInventoryChanges,
  storeProducts,
  moneyStart,
  moneyEnd,
  snapshotsEnd,
  debts,
  purchaseOrders,
  dailyCountLists,
}: SummarySlideProps) {
  const { t } = useSharedUiTranslation();
  const merchant = useRecoilValue(merchantAtom);
  const store = useRecoilValue(storeAtom);

  const salesDrawer = useDisclosure();
  const grossProfitDrawer = useDisclosure();
  const expensesDrawer = useDisclosure();
  const inventoryValueOverlay = useDisclosure();
  const inventoryGainLossDrawer = useDisclosure();
  const supplierDebtsDrawer = useDisclosure();
  const clientDebtsDrawer = useDisclosure();
  const moneyDetailsDrawer = useDisclosure();
  const zeroAccountsCollapsible = useDisclosure();

  // Show a debt card only when that debt exists. Supplier due can be negative
  // (the store is in credit with its suppliers) and that is still worth
  // showing, so the test is "non-zero", not "greater than zero"; money paid to
  // suppliers in the period counts as activity too. A card whose request
  // failed is shown too, so the failure is never mistaken for "no debt".
  const hasSupplierActivity =
    supplierMetrics.due !== 0 ||
    supplierMetrics.paid !== 0 ||
    status.supplierValuation.isError;
  const hasClientDebts =
    debtMetrics.outstanding !== 0 ||
    debtMetrics.paid !== 0 ||
    status.debts.isError;

  const [selectedEmployee, setSelectedEmployee] = useState<{
    accountId: string;
    name: string;
  } | null>(null);

  const handleMoneyDrawerClose = () => {
    moneyDetailsDrawer.onClose();
    setSelectedEmployee(null);
  };

  const openMoneyDrawerForAll = () => {
    setSelectedEmployee(null);
    moneyDetailsDrawer.onOpen();
  };

  const openMoneyDrawerForEmployee = (accountId: string, name: string) => {
    setSelectedEmployee({ accountId, name });
    moneyDetailsDrawer.onOpen();
  };

  // Fetch users for employee name resolution (shared cache with MoneyDetailsDrawer)
  const { data: usersData } = useQuery(
    ['users', merchant?.id, store?.id],
    () =>
      apiAdapter.getUsers({
        merchant_id: merchant!.id,
        store_id: store!.id,
        limit: 100,
      }),
    {
      enabled: !!merchant?.id && !!store?.id,
      staleTime: 5 * 60 * 1000,
    },
  );
  const users = usersData?.employees ?? [];

  // Fetch active shifts (shared cache with MoneyDetailsDrawer)
  const { data: activeShifts } = useQuery(
    ['activeShiftsForMoney', merchant?.id, store?.id],
    async () => {
      const statuses = [
        ShiftStatus.OPEN,
        ShiftStatus.PENDING_CLOSE,
        ShiftStatus.NEEDS_ATTENTION,
      ];
      const results = await Promise.all(
        statuses.map(async (status) => {
          const page = await apiAdapter.getShifts({
            merchant_id: merchant!.id,
            store_id: store!.id,
            status,
            by_status: true,
            limit: 50,
          });
          return page.shifts ?? [];
        }),
      );
      return results.flat();
    },
    { enabled: !!merchant?.id && !!store?.id, staleTime: 60 * 1000 },
  );

  // Map employee account_id → their open shift
  const activeShiftMap = useMemo(() => {
    const map = new Map<string, Shift>();
    if (activeShifts) {
      for (const shift of activeShifts) {
        map.set(shift.opened_by, shift);
      }
    }
    return map;
  }, [activeShifts]);

  // Sum closing-correction transactions per employee from the date-range transactions
  const closingCorrectionMap = useMemo(() => {
    const map = new Map<string, Big>();
    for (const tx of accountTransactions) {
      if (tx.category !== TransactionCategory.CLOSING_CORRECTION) continue;
      const prev = map.get(tx.account_id) ?? new Big(0);
      map.set(tx.account_id, prev.plus(tx.amount));
    }
    return map;
  }, [accountTransactions]);

  const expenseTransactions = useMemo(
    () =>
      accountTransactions.filter(
        (tx) =>
          canonicalTransactionCategory(tx.category) ===
          TransactionCategory.EXPENSE,
      ),
    [accountTransactions],
  );

  // Compute payment-method breakdown as a single subtitle string
  const paymentMethodSubtitle = useMemo(() => {
    if (!snapshotsEnd?.snapshots) return '';
    const totals = new Map<string, number>();
    for (const userSnap of snapshotsEnd.snapshots) {
      for (const snap of userSnap.account_snapshots) {
        const key = snap.payment_type;
        totals.set(
          key,
          new Big(totals.get(key) || 0).plus(snap.amount).toNumber(),
        );
      }
    }
    const methodLabels = getMethodLabels(t);
    return Array.from(totals.entries())
      .filter(([, amount]) => amount !== 0)
      .sort((a, b) => b[1] - a[1])
      .map(
        ([method, amount]) =>
          `${methodLabels[method] || method}: ${formatCurrency(amount, true)}`,
      )
      .join(', ');
  }, [snapshotsEnd, t]);

  // Per-employee money breakdown
  const employeeRows = useMemo(() => {
    if (!snapshotsEnd?.snapshots || snapshotsEnd.snapshots.length <= 1)
      return [];

    const userMap = new Map<string, string>();
    for (const u of users) userMap.set(u.id, u.name);
    const methodLabels = getMethodLabels(t);

    return snapshotsEnd.snapshots
      .map((userSnap) => {
        const endMoney = bigSum(
          userSnap.account_snapshots.map((s) => s.amount),
        ).toNumber();
        const pmBreakdown = userSnap.account_snapshots
          .filter((s) => s.amount !== 0)
          .sort((a, b) => b.amount - a.amount)
          .map(
            (s) =>
              `${methodLabels[s.payment_type] || s.payment_type}: ${formatCurrency(s.amount, true)}`,
          )
          .join(', ');
        const shift = activeShiftMap.get(userSnap.account_id);
        const shiftOpenedAt = shift?.opened_at ?? null;
        const isShiftStale = shiftOpenedAt
          ? Date.now() - new Date(shiftOpenedAt).getTime() > 24 * 60 * 60 * 1000
          : false;
        const hasOpenShift = !!shift;
        const totalVariance =
          closingCorrectionMap.get(userSnap.account_id)?.toNumber() ?? 0;
        return {
          accountId: userSnap.account_id,
          name: userMap.get(userSnap.account_id) || t('Unknown'),
          endMoney,
          pmBreakdown,
          shiftOpenedAt,
          isShiftStale,
          hasOpenShift,
          totalVariance,
        };
      })
      .sort((a, b) => b.endMoney - a.endMoney);
  }, [snapshotsEnd, users, activeShiftMap, closingCorrectionMap, t]);

  // Daily count health: red (<50%), orange (50-80%), green (>80%), null if disabled
  const dailyCountHealth = useMemo<'red' | 'orange' | 'green' | null>(() => {
    if (!store?.daily_count_enabled || !dailyCountLists?.length) return null;
    if (!dateRange.start || !dateRange.end) return null;

    const dateMap = new Map<string, DailyCountList>();
    for (const l of dailyCountLists) dateMap.set(l.count_date, l);

    // Iterate each day in range — only score days that have a daily count list
    const d = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const pcts: number[] = [];
    while (d <= end) {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const list = dateMap.get(key);
      if (list) {
        const counted = list.items.filter(
          (i) => i.status === DailyCountItemStatus.COUNTED,
        ).length;
        const target = list.target_count || list.items.length || 1;
        pcts.push((counted / target) * 100);
      }
      d.setDate(d.getDate() + 1);
    }

    if (!pcts.length) return null;
    const avg = pcts.reduce((s, v) => s + v, 0) / pcts.length;
    if (avg < 50) return 'red';
    if (avg < 80) return 'orange';
    return 'green';
  }, [dailyCountLists, dateRange, store?.daily_count_enabled]);

  return (
    <>
      {/* ── Sales Performance ── */}
      <SectionHeader color="blue.400" label={t('Sales Performance')} />
      {salesReportError ? (
        <Box mb={1.5}>
          <QueryErrorAlert
            error={salesReportError}
            onRetry={onRetrySalesReport}
            isRetrying={isRetryingSalesReport}
          />
        </Box>
      ) : null}
      {/* Sales-dependent cards are hidden while the report request has
          failed: the metrics hook defaults a missing report to 0, and a
          zero here would read as "no sales". Expenses come from a separate
          request and stay visible. */}
      <SimpleGrid columns={2} spacing={1} mb={1.5} w="full">
        {!salesReportError && (
          <MetricCard
            label={t('Net Sales')}
            value={salesMetrics.netSales}
            {...status.salesReport}
            positiveColor="green.600"
            negativeColor="red.500"
            neutralColor={salesMetrics.netSales > 0 ? 'green.600' : 'red.500'}
            onClick={salesDrawer.onOpen}
          />
        )}
        {!salesReportError && (
          <MetricCard
            label={t('Gross Profit')}
            value={salesMetrics.grossProfit}
            {...status.salesReport}
            positiveColor="green.600"
            negativeColor="red.500"
            neutralColor={
              salesMetrics.grossProfit > 0 ? 'green.600' : 'red.500'
            }
            onClick={grossProfitDrawer.onOpen}
          />
        )}
        <MetricCard
          label={t('Expenses')}
          value={-expenses}
          {...status.accountTransactions}
          positiveColor="red.600"
          negativeColor="gray.600"
          neutralColor={expenses > 0 ? 'red.600' : 'gray.600'}
          onClick={expensesDrawer.onOpen}
        />
        {!salesReportError && (
          <MetricCard
            label={t('Net Profit')}
            value={netProfit}
            {...combineStatus(status.salesReport, status.accountTransactions)}
            positiveColor="green.600"
            negativeColor="red.500"
            neutralColor={
              netProfit > 0
                ? 'green.600'
                : netProfit < 0
                  ? 'red.500'
                  : 'gray.600'
            }
            onClick={salesDrawer.onOpen}
          />
        )}
      </SimpleGrid>

      {/* ── Inventory ── */}
      <SectionHeader color="teal.400" label={t('Inventory')} />
      <SimpleGrid columns={2} spacing={1} mb={1.5} w="full">
        <MetricCard
          label={t('Inventory Value')}
          value={storeValuation.inventoryEnd}
          {...status.inventoryValues}
          neutralColor="teal.600"
          onClick={inventoryValueOverlay.onOpen}
        />
        <Box position="relative">
          <MetricCard
            label={t('Count/Correction')}
            value={inventoryGainOrLoss}
            {...status.inventoryChanges}
            positiveColor="green.600"
            negativeColor="red.500"
            onClick={inventoryGainLossDrawer.onOpen}
          />
          {dailyCountHealth && (
            <Icon
              as={
                dailyCountHealth === 'green'
                  ? FaCheckCircle
                  : FaExclamationTriangle
              }
              position="absolute"
              top={1}
              right={1}
              boxSize={3.5}
              color={
                dailyCountHealth === 'red'
                  ? 'red.500'
                  : dailyCountHealth === 'orange'
                    ? 'orange.400'
                    : 'green.500'
              }
              css={
                dailyCountHealth !== 'green'
                  ? {
                      animation: `${blinkAnimation} 1s ease-in-out infinite`,
                    }
                  : undefined
              }
            />
          )}
        </Box>
      </SimpleGrid>

      {/* ── Money ── */}
      <SectionHeader color="purple.400" label={t('Money')} />
      <SimpleGrid columns={1} spacing={1} mb={1.5} w="full">
        <MetricCard
          label={t('Available Money')}
          value={moneyEnd}
          {...status.snapshotsEnd}
          neutralColor="purple.600"
          subtitle={paymentMethodSubtitle || undefined}
          subtitleColor="gray.500"
          onClick={openMoneyDrawerForAll}
        />
      </SimpleGrid>

      {/* ── Per-employee money breakdown ── */}
      {employeeRows.length > 0 &&
        (() => {
          // Show rows with money, an open shift, or a closing variance.
          // Only collapse rows that are truly idle: zero money, no open shift, no variance.
          const visibleRows = employeeRows.filter(
            (emp) =>
              emp.endMoney !== 0 || emp.hasOpenShift || emp.totalVariance !== 0,
          );
          const hiddenRows = employeeRows.filter(
            (emp) =>
              emp.endMoney === 0 &&
              !emp.hasOpenShift &&
              emp.totalVariance === 0,
          );
          const formatShiftTime = (iso: string) => {
            const d = new Date(iso);
            const day = d.getDate();
            const month = d.toLocaleString(getIntlLocale(), { month: 'short' });
            const year = d.getFullYear();
            const hours = d.getHours().toString().padStart(2, '0');
            const mins = d.getMinutes().toString().padStart(2, '0');
            return t('{{date}} at {{time}}', {
              date: `${day} ${month} ${year}`,
              time: `${hours}:${mins}`,
            });
          };
          const renderRow = (emp: (typeof employeeRows)[number]) => (
            <HStack
              key={emp.accountId}
              px={2}
              py={1}
              bg={emp.isShiftStale ? 'red.100' : 'gray.50'}
              borderRadius="sm"
              cursor="pointer"
              _hover={{ bg: emp.isShiftStale ? 'red.200' : 'gray.100' }}
              onClick={() =>
                openMoneyDrawerForEmployee(emp.accountId, emp.name)
              }>
              <Icon
                as={emp.hasOpenShift ? FaLockOpen : FaLock}
                boxSize={3.5}
                color={
                  emp.hasOpenShift
                    ? emp.isShiftStale
                      ? 'red.700'
                      : 'blue.500'
                    : 'green.500'
                }
                mt={0.5}
                flexShrink={0}
              />
              <VStack spacing={0} align="start" flex={1}>
                <Text fontSize="xx-small" fontWeight="medium">
                  {emp.name}
                </Text>
                {emp.shiftOpenedAt && (
                  <Text
                    fontSize="xx-small"
                    color={emp.isShiftStale ? 'red.600' : 'gray.500'}
                    fontWeight={emp.isShiftStale ? 'semibold' : 'normal'}>
                    {t('Open since {{date}}', {
                      date: formatShiftTime(emp.shiftOpenedAt),
                    })}
                  </Text>
                )}
                {emp.pmBreakdown && (
                  <Text fontSize="x-small" color="gray.400" width={'full'}>
                    {emp.pmBreakdown}
                  </Text>
                )}
              </VStack>
              <VStack spacing={0} align="end" flexShrink={0}>
                <Text fontSize="sm" fontWeight="bold" color="purple.700">
                  {formatCurrency(emp.endMoney, true)}
                </Text>
                {emp.totalVariance !== 0 && (
                  <HStack spacing={1} align="center">
                    <Spacer />
                    <Icon
                      as={FaExclamationTriangle}
                      boxSize={2.5}
                      color="red.500"
                      css={{
                        animation: `${blinkAnimation} 1s ease-in-out infinite`,
                      }}
                    />

                    <Text fontSize="x-small" fontWeight="bold" color="red.600">
                      {formatCurrency(emp.totalVariance, true)}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </HStack>
          );
          return (
            <VStack spacing={0.5} align="stretch" mb={1.5} w="full">
              {visibleRows.map(renderRow)}
              {hiddenRows.length > 0 && (
                <>
                  <Text
                    fontSize="xx-small"
                    color="gray.400"
                    cursor="pointer"
                    textAlign="center"
                    py={0.5}
                    _hover={{ color: 'gray.600' }}
                    onClick={zeroAccountsCollapsible.onToggle}>
                    {zeroAccountsCollapsible.isOpen
                      ? t('Hide zero balance accounts', {
                          count: hiddenRows.length,
                        })
                      : t('Show {{count}} zero balance accounts', {
                          count: hiddenRows.length,
                        })}
                  </Text>
                  <Collapse in={zeroAccountsCollapsible.isOpen} animateOpacity>
                    <VStack spacing={0.5} align="stretch">
                      {hiddenRows.map(renderRow)}
                    </VStack>
                  </Collapse>
                </>
              )}
            </VStack>
          );
        })()}

      {/* ── Payments & Debts ──
          Each card appears only when that debt actually exists, and the
          section disappears entirely when neither does, so a store that owes
          nothing and is owed nothing is not shown a row of zeroes. Supplier
          due is the same receive-based figure as the Valuation tab, the
          Supplier Debts page and the Purchase Orders app. */}
      {(hasSupplierActivity || hasClientDebts) && (
        <>
          <SectionHeader color="red.400" label={t('Payments & Debts')} />
          <SimpleGrid
            columns={hasSupplierActivity && hasClientDebts ? 2 : 1}
            spacing={1}
            w="full">
            {hasSupplierActivity && (
              <MetricCard
                label={t('Paid to Suppliers')}
                value={supplierMetrics.paid}
                {...status.supplierValuation}
                positiveColor="red.500"
                negativeColor="green.600"
                neutralColor={
                  supplierMetrics.paid > 0 ? 'red.500' : 'green.600'
                }
                subtitle={t('Due: {{amount}}', {
                  amount: formatCurrency(supplierMetrics.due, true),
                })}
                subtitleColor="red.500"
                onClick={supplierDebtsDrawer.onOpen}
              />
            )}
            {hasClientDebts && (
              <MetricCard
                label={t('Client Debts')}
                value={debtMetrics.outstanding}
                {...status.debts}
                positiveColor="red.500"
                negativeColor="green.500"
                neutralColor={
                  debtMetrics.outstanding > 0 ? 'red.500' : 'green.500'
                }
                subtitle={
                  debtMetrics.paid > 0
                    ? t('Collected: {{amount}}', {
                        amount: formatCurrency(debtMetrics.paid, true),
                      })
                    : undefined
                }
                subtitleColor="green.500"
                onClick={clientDebtsDrawer.onOpen}
              />
            )}
          </SimpleGrid>
        </>
      )}

      {/* ── Drawers ── */}
      <SalesReportDrawer
        isOpen={salesDrawer.isOpen}
        onClose={salesDrawer.onClose}
        dateRange={dateRange}
      />

      <GrossProfitDrawer
        isOpen={grossProfitDrawer.isOpen}
        onClose={grossProfitDrawer.onClose}
        storeProducts={storeProducts}
      />

      <TransactionCategoryDrawer
        isOpen={expensesDrawer.isOpen}
        onClose={expensesDrawer.onClose}
        category={TransactionCategory.EXPENSE}
        transactions={expenseTransactions}
        reversals={mergeReversalCandidates(
          accountTransactionsReversals,
          accountTransactions,
        )}
      />

      <InventoryValueOverlay
        isOpen={inventoryValueOverlay.isOpen}
        onClose={inventoryValueOverlay.onClose}
        inventoryValues={inventoryValues}
        dateRange={dateRange}
        inventoryChanges={allInventoryChanges}
        isLoading={status.inventoryValues.isLoading}
        storeProducts={storeProducts}
        costRevaluation={costRevaluation}
      />

      <InventoryGainLossDrawer
        isOpen={inventoryGainLossDrawer.isOpen}
        onClose={inventoryGainLossDrawer.onClose}
        inventoryChanges={allInventoryChanges}
        storeProducts={storeProducts}
        dailyCountLists={dailyCountLists}
        dateRange={dateRange}
      />

      <SupplierDebtsPageDrawer
        isOpen={supplierDebtsDrawer.isOpen}
        onClose={supplierDebtsDrawer.onClose}
        dateRange={dateRange}
      />

      <ClientDebtsPageDrawer
        isOpen={clientDebtsDrawer.isOpen}
        onClose={clientDebtsDrawer.onClose}
        dateRange={dateRange}
      />

      <MoneyDetailsDrawer
        isOpen={moneyDetailsDrawer.isOpen}
        onClose={handleMoneyDrawerClose}
        dateRange={dateRange}
        accountTransactions={accountTransactions}
        reversals={accountTransactionsReversals}
        moneyStart={moneyStart}
        moneyEnd={moneyEnd}
        snapshotsEnd={snapshotsEnd}
        employeeFilter={selectedEmployee}
        debts={debts}
        purchaseOrders={purchaseOrders}
      />
    </>
  );
}
